import React, { useEffect, useState } from 'react'
import { getMerchants } from '../core/api'
import { nf } from '../core/consts'
import { METRIC_HINTS } from '../core/hints'
import { MerchantRow } from '../core/types'
import InfoHint from '../../../../../common/InfoHint'

type Props = {
  currency: string
  /** Bumped by the page on every refresh so the block reloads with the rest. */
  refreshToken: number
}

/**
 * Cascade threshold cell. Three distinct states that must not be collapsed:
 *   disabled -> «отключен» (no enabled cascade row: matching really is off here)
 *   0        -> «—»        (enabled, simply no lower bound)
 *   n        -> the number
 * Rendering 0 as «отключен» would misreport live configuration.
 */
const threshold = (value: number | null, disabled: boolean) => {
  if (disabled) return <span className='badge badge-light-danger'>отключен</span>
  if (value === null || value === 0) return <span className='text-muted'>—</span>
  return nf.format(value)
}

/**
 * Live per-merchant slice (CPAYMENT-1689). State columns carry no time window;
 * the two refusal columns are events and say «за 24 ч» in their headers — see
 * MerchantRow in core/types.ts for why the two kinds cannot share a window.
 */
const MerchantsBlock: React.FC<Props> = ({ currency, refreshToken }) => {
  const [rows, setRows] = useState<MerchantRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getMerchants(currency)
      .then((resp) => {
        if (cancelled) return
        if (resp.data.success) {
          setRows(resp.data.data?.merchants ?? [])
          setError(null)
        } else {
          setError('Не удалось загрузить срез по мерчантам')
        }
      })
      .catch(() => !cancelled && setError('Сервис мэтчинга недоступен'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [currency, refreshToken])

  return (
    <div className='card mb-5'>
      <div className='card-header'>
        <h3 className='card-title d-inline-flex align-items-center'>
          Мерчанты ({currency})
          <InfoHint text={METRIC_HINTS.merchants} />
        </h3>
      </div>
      <div className='card-body'>
        {error ? (
          <div className='text-danger'>{error}</div>
        ) : rows.length === 0 ? (
          <div className='text-muted'>
            {loading ? 'Загрузка…' : 'Нет мерчантов с активностью в выбранной валюте'}
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-sm table-row-bordered align-middle'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th>Мерчант</th>
                  <th className='text-end'>Выплаты в работе</th>
                  <th className='text-end'>Остаток</th>
                  <th className='text-end'>Депозиты в работе</th>
                  <th className='text-end'>Спрос</th>
                  <th className='text-end'>Оплачено депозитов</th>
                  <th className='text-end'>Заполнение</th>
                  <th className='text-end'>Выплаты не взяты, 24 ч</th>
                  <th className='text-end'>Депозиты не взяты, 24 ч</th>
                  <th className='text-end'>
                    <span className='d-inline-flex align-items-center'>
                      Мин. депозит
                      <InfoHint text={METRIC_HINTS.merchants_min_amounts} />
                    </span>
                  </th>
                  <th className='text-end'>Мин. выплата</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.merchant_id}>
                    <td>{r.merchant || `#${r.merchant_id}`}</td>
                    <td className='text-end'>{nf.format(r.open_payouts_count)}</td>
                    <td className='text-end'>
                      {nf.format(Math.round(r.open_payouts_remainder))}
                    </td>
                    <td className='text-end'>{nf.format(r.live_deposits_count)}</td>
                    <td className='text-end'>{nf.format(Math.round(r.live_deposits_amount))}</td>
                    <td className='text-end'>{nf.format(r.completed_deposits_in_open)}</td>
                    <td className='text-end'>
                      {r.open_payouts_count > 0 ? `${r.open_payouts_fill_pct.toFixed(0)}%` : '—'}
                    </td>
                    <td className='text-end'>{nf.format(r.flowcontrol_rejects_24h)}</td>
                    <td className='text-end'>{nf.format(r.deposits_no_payout_24h)}</td>
                    <td className='text-end'>{threshold(r.minDeposit, r.minDepositDisabled)}</td>
                    <td className='text-end'>{threshold(r.minPayout, r.minPayoutDisabled)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className='text-muted fs-8 mt-2'>
          Показатели состояния — на текущий момент, без окна: выплаты мэтчинга могут висеть
          открытыми дольше суток. Две колонки «не взяты в работу» — события, поэтому за
          последние 24 часа.
        </div>
      </div>
    </div>
  )
}

export default MerchantsBlock
